import { intlFormat } from 'date-fns';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { DailyUsage } from '#dn-types/billing.ts';
import { usePrevious } from '#hooks/usePrevious.ts';
import styles from './HostUsageChart.module.css';

const CHART_WIDTH = 550;
const CHART_HEIGHT = 200;
const CHART_TOP_PADDING = 120; // Needs to be big enough when scaled down (text is constant size)
const TOTAL_HEIGHT = CHART_HEIGHT + CHART_TOP_PADDING;
const GUTTER = 5; // Spacing between the bar
const MIN_BAR_HEIGHT = 2;
// We want to round the top corners by this amount, but also have to shift the bottom down this far
// because it applies to all corners, not just the top
const BAR_RADIUS = 3;

type Props = {
  usage: DailyUsage[];
};

/**
 * Shows a historical trend of enrolled hosts within the current billing period,
 * to help users understand their estimated bill.
 */
export function HostUsageChart({ usage }: Props) {
  const [scale, setScale] = useState<number>(1);
  const chartRef = useRef<SVGSVGElement>(null);
  const prevScale = usePrevious(scale);

  const checkScale = useCallback(
    (element: SVGSVGElement) => {
      const { clientWidth } = element;
      const newScale = clientWidth / CHART_WIDTH;
      // Only set a scale if it's changed, and isn't the previous scale (due to scollbar hide/show)
      if (Math.abs(newScale - scale) > 0.001 && Math.abs(newScale - (prevScale ?? 0)) > 0.001) {
        setScale(newScale);
      }
    },
    [scale, prevScale]
  );

  const makeResizeObserver = useCallback(
    (ref: React.RefObject<SVGSVGElement | null>) =>
      new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target instanceof SVGSVGElement && entry.target === ref.current) {
            checkScale(entry.target);
          }
        }
      }),
    [checkScale]
  );

  // Listen for changes to the size of the SVG, so that we can apply an inverse scale
  // to the text and keep them a constant size
  useLayoutEffect(() => {
    // Only set up a resizeObserver if we have an element to observe
    if (chartRef.current) {
      // Set the value right away, without waiting for resize observer to fire,
      // so that we can get the value before the browser paints the first time.
      checkScale(chartRef.current);

      const resizeObserver = makeResizeObserver(chartRef);

      resizeObserver.observe(chartRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [scale, checkScale, makeResizeObserver]);

  const daysInPeriod = usage.length;
  const barWidth = (CHART_WIDTH - GUTTER * daysInPeriod) / daysInPeriod;
  // Default to 1, otherwise we could end up dividing by zero later
  let maxCost = 1;
  for (const day of usage) {
    const dayUsage = day.cost;
    if (dayUsage && dayUsage > maxCost) {
      maxCost = dayUsage;
    }
  }

  const bars = usage.map((use, idx) => {
    // Use zero for future usage
    const cents = use.cost ?? 0;
    return (
      <HostUsageChartBar
        cents={cents}
        date={use.date}
        key={use.date}
        width={barWidth}
        height={Math.max((cents / maxCost) * CHART_HEIGHT, MIN_BAR_HEIGHT)}
        scale={scale}
        index={idx}
        gutter={GUTTER}
      />
    );
  });

  return (
    <div className={styles.HostUsageChart_container}>
      <svg
        className={styles.HostUsageChart}
        ref={chartRef}
        width={CHART_WIDTH}
        height={TOTAL_HEIGHT * scale}
        // The y-min number here is the negative chart height, so we can invert the y axis and show bars starting at the bottom
        viewBox={`0 ${-1 * TOTAL_HEIGHT} ${CHART_WIDTH} ${TOTAL_HEIGHT}`}
        role="img"
      >
        <title>A bar chart showing usage per day of billing period</title>
        <g className={styles.HostUsageChart_title} transform={`translate(0,${getTextTranslateY(scale)})`}>
          <text transform={`scale(${1 / scale})`}>Daily usage</text>
        </g>
        {bars}
      </svg>
    </div>
  );
}

type BarProps = {
  /** Width of the bar */
  width: number;
  /** Height of the bar */
  height: number;
  /** The horizontal space between each bar */
  gutter: number;
  /** Starting from the left, which position is this bar in? */
  index: number;
  /** Number of cents of usage for this day */
  cents: number;
  /** ISO 8601 day-only */
  date: string;
  /** When the chart resizes, we need to apply an inverse scale to keep the text size constant */
  scale: number;
};

// Breaking out for simplicity, but keeping in this file for css and constants
function HostUsageChartBar({ width, height, gutter, index, cents, date, scale }: BarProps) {
  const dateString = intlFormat(new Date(date), {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'utc',
  });
  const dollars = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);

  return (
    <g>
      <rect
        className={styles.HostUsageChart_bar}
        tabIndex={0}
        width={width}
        height={height + BAR_RADIUS}
        x={(width + gutter) * index}
        y={-BAR_RADIUS}
        rx={BAR_RADIUS}
      >
        <title>
          {dollars} usage on {dateString} UTC
        </title>
      </rect>
      <g
        className={styles.HostUsageChart_barTextGroup}
        transform={`translate(${CHART_WIDTH},${getTextTranslateY(scale)})`}
      >
        <text className={styles.HostUsageChart_barHostCount} transform={`scale(${1 / scale})`}>
          {dollars}
        </text>
        <text className={styles.HostUsageChart_barDate} transform={`scale(${1 / scale})`} dy={18}>
          {dateString} UTC
        </text>
      </g>
    </g>
  );
}

/** Returns a y translation to use for positioning the header text */
function getTextTranslateY(scale: number) {
  const fontSize = 20; // h4
  return -(TOTAL_HEIGHT - fontSize * (1 / scale));
}
